import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { apiErrorMessage } from "../lib/errors";

type Platform = "twitter" | "bluesky" | "linkedin";

interface Bucket {
  utc_day: number;
  utc_hour: number;
  post_count: number;
  avg_score: number;
  best_score: number;
  total_likes: number;
  total_replies: number;
  total_reposts: number;
}

interface OptimalTimesResponse {
  platform: Platform | null;
  days: number;
  total_posts: number;
  buckets: Bucket[];
}

interface LocalCell {
  day: number;
  hour: number;
  post_count: number;
  avg_score: number;
  best_score: number;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function detectTimezone(): { tz: string; offsetMinutes: number } {
  const tz =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "UTC";
  const offsetMinutes = -new Date().getTimezoneOffset();
  return { tz, offsetMinutes };
}

function shiftBucketsToLocal(
  buckets: Bucket[],
  offsetMinutes: number
): LocalCell[] {
  const cells = new Map<string, LocalCell>();
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      cells.set(`${d}-${h}`, {
        day: d,
        hour: h,
        post_count: 0,
        avg_score: 0,
        best_score: 0,
      });
    }
  }
  const offsetHours = offsetMinutes / 60;
  for (const b of buckets) {
    let totalMinutes = b.utc_day * 24 * 60 + b.utc_hour * 60 + offsetMinutes;
    const minutesInWeek = 7 * 24 * 60;
    totalMinutes = ((totalMinutes % minutesInWeek) + minutesInWeek) % minutesInWeek;
    const localDay = Math.floor(totalMinutes / (24 * 60));
    const localHour = Math.floor((totalMinutes % (24 * 60)) / 60);
    const key = `${localDay}-${localHour}`;
    const existing = cells.get(key);
    if (existing) {
      existing.post_count += b.post_count;
      existing.avg_score =
        (existing.avg_score * (existing.post_count - b.post_count) +
          b.avg_score * b.post_count) /
        Math.max(existing.post_count, 1);
      existing.best_score = Math.max(existing.best_score, b.best_score);
    }
  }
  void offsetHours;
  return Array.from(cells.values());
}

function bucketColor(cell: LocalCell, maxAvg: number): string {
  if (cell.post_count === 0) return "#f1f5f9";
  const intensity = maxAvg > 0 ? cell.avg_score / maxAvg : 0;
  const a = Math.max(0.12, Math.min(1, 0.18 + intensity * 0.82));
  return `rgba(79, 70, 229, ${a.toFixed(2)})`;
}

function formatHour(h: number): string {
  if (h === 0) return "12a";
  if (h === 12) return "12p";
  return h < 12 ? `${h}a` : `${h - 12}p`;
}

export function OptimalTimes(props: { defaultPlatform?: Platform }) {
  const toast = useToast();
  const [platform, setPlatform] = useState<Platform>(
    props.defaultPlatform ?? "bluesky"
  );
  const [days, setDays] = useState(90);
  const [data, setData] = useState<OptimalTimesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const tzInfo = useMemo(() => detectTimezone(), []);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get<OptimalTimesResponse>(
        `/api/analytics/optimal-times?platform=${platform}&days=${days}`
      );
      setData(res);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, days]);

  const cells = useMemo(() => {
    if (!data) return [];
    return shiftBucketsToLocal(data.buckets, tzInfo.offsetMinutes);
  }, [data, tzInfo.offsetMinutes]);

  const maxAvg = useMemo(
    () => cells.reduce((m, c) => Math.max(m, c.avg_score), 0),
    [cells]
  );

  const top5 = useMemo(() => {
    return cells
      .filter((c) => c.post_count > 0)
      .sort((a, b) => b.avg_score - a.avg_score)
      .slice(0, 5);
  }, [cells]);

  const totalPosts = data?.total_posts ?? 0;

  return (
    <section className="mb-8">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-base font-medium text-ink-900">Optimal time</h2>
          <p className="text-xs text-ink-500">
            Based on your historical engagement. Times shown in your timezone (
            {tzInfo.tz}).
          </p>
        </div>
        <div className="flex gap-2">
          <select
            className="input inline-block w-auto py-1 text-xs"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as Platform)}
          >
            <option value="bluesky">Bluesky</option>
            <option value="twitter">X / Twitter</option>
            <option value="linkedin">LinkedIn</option>
          </select>
          <select
            className="input inline-block w-auto py-1 text-xs"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
          >
            <option value={30}>30d window</option>
            <option value={60}>60d window</option>
            <option value={90}>90d window</option>
            <option value={180}>180d window</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-ink-200 bg-white p-6 text-center text-sm text-ink-500">
          Loading…
        </div>
      ) : totalPosts < 3 ? (
        <div className="rounded-xl border border-dashed border-ink-200 bg-white p-6 text-center text-sm text-ink-500">
          Need at least 3 published posts on this platform to compute optimal times.
          You currently have {totalPosts}.
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-ink-200 bg-white p-3">
            <div className="overflow-x-auto">
              <div
                className="grid"
                style={{
                  gridTemplateColumns: "auto repeat(24, minmax(20px, 1fr))",
                  gap: "2px",
                }}
              >
                <div></div>
                {Array.from({ length: 24 }).map((_, h) => (
                  <div
                    key={`h-${h}`}
                    className="text-center text-[10px] text-ink-400"
                  >
                    {h % 3 === 0 ? formatHour(h) : ""}
                  </div>
                ))}
                {DAY_LABELS.map((label, dayIdx) => (
                  <DayRow
                    key={label}
                    label={label}
                    dayIdx={dayIdx}
                    cells={cells.filter((c) => c.day === dayIdx)}
                    maxAvg={maxAvg}
                  />
                ))}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-ink-500">
              <span>{totalPosts} posts in last {days}d</span>
              <div className="flex items-center gap-2">
                <span>Less</span>
                <div
                  className="h-3 w-4 rounded"
                  style={{ background: bucketColor({ post_count: 1, avg_score: maxAvg * 0.05, best_score: 0, day: 0, hour: 0 }, maxAvg) }}
                />
                <div
                  className="h-3 w-4 rounded"
                  style={{ background: bucketColor({ post_count: 1, avg_score: maxAvg * 0.4, best_score: 0, day: 0, hour: 0 }, maxAvg) }}
                />
                <div
                  className="h-3 w-4 rounded"
                  style={{ background: bucketColor({ post_count: 1, avg_score: maxAvg, best_score: 0, day: 0, hour: 0 }, maxAvg) }}
                />
                <span>More</span>
              </div>
            </div>
          </div>

          {top5.length > 0 ? (
            <div className="mt-3 rounded-xl border border-ink-200 bg-white p-4">
              <h3 className="mb-2 text-sm font-medium text-ink-900">
                Top {top5.length} time slots for you
              </h3>
              <ol className="space-y-1.5 text-sm">
                {top5.map((c, i) => (
                  <li key={`${c.day}-${c.hour}`} className="flex items-baseline gap-2">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-800">
                      {i + 1}
                    </span>
                    <span className="font-medium text-ink-900">
                      {DAY_LABELS[c.day]} {formatHour(c.hour)}
                    </span>
                    <span className="text-xs text-ink-500">
                      avg engagement {c.avg_score.toFixed(1)} · {c.post_count}{" "}
                      post{c.post_count > 1 ? "s" : ""}
                    </span>
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-xs text-ink-400">
                As you publish more posts, these recommendations get sharper.
              </p>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function DayRow(props: {
  label: string;
  dayIdx: number;
  cells: LocalCell[];
  maxAvg: number;
}) {
  const cellByHour = new Map(props.cells.map((c) => [c.hour, c]));
  return (
    <>
      <div className="pr-2 text-right text-xs font-medium text-ink-600">
        {props.label}
      </div>
      {Array.from({ length: 24 }).map((_, h) => {
        const cell = cellByHour.get(h) ?? {
          day: props.dayIdx,
          hour: h,
          post_count: 0,
          avg_score: 0,
          best_score: 0,
        };
        const tip =
          cell.post_count > 0
            ? `${props.label} ${formatHour(h)} · ${cell.post_count} post(s) · avg ${cell.avg_score.toFixed(1)}`
            : `${props.label} ${formatHour(h)} · no posts`;
        return (
          <div
            key={`${props.dayIdx}-${h}`}
            className="h-6 rounded-sm"
            style={{ background: bucketColor(cell, props.maxAvg) }}
            title={tip}
          />
        );
      })}
    </>
  );
}
